import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { UiAvatarSize } from "@webapp/ui/avatar/avatar.models";
import { UiConfigurationService } from "@webapp/ui/core/services/configuration.service";
import { UiThemeType } from "@webapp/ui/icon/icon.models";

/**
 * Avatars can be used to represent people or objects. It supports pictures or icons.
 * If both picture and icon is specified, the former takes advantage.
 *
 * Jira epic: https://gtmhub.atlassian.net/browse/GVS-15082
 *
 * Specification: https://gtmhub.atlassian.net/l/cp/K2HTMDAA
 *
 * Design: https://www.figma.com/file/Uopb68lCUHeN4rQ9qKetLb/Design-System-3.0?node-id=233%3A2413
 */
@Component({
  selector: "ui-avatar",
  exportAs: "uiAvatar",
  templateUrl: "avatar.component.html",
  styleUrls: ["./avatar.component.less"],
  host: {
    "[class.ant-avatar]": `true`,
    "[class.ant-avatar-circle]": `true`,
    "[class.ui-avatar-x-small]": `size === 'xs'`,
    "[class.ui-avatar-small]": `size === 'sm'`,
    "[class.ui-avatar-medium]": `size === 'md'`,
    "[class.ui-avatar-large]": `size === 'lg'`,
    "[class.ui-avatar-x-large]": `size === 'xl'`,
    "[class.ui-avatar-xx-large]": `size === 'xxl'`,
    "[class.ui-avatar-disabled]": `disabled`,
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiAvatarComponent implements OnChanges {
  /**
   * @ignore
   */
  public patternSrc?: string;

  /**
   * @ignore
   */
  public realAlt: string;

  @Input("uiSize")
  public size: UiAvatarSize = "xs";

  @Input("uiPicture")
  public picture?: string;

  @Input("uiAlt")
  public alt?: string;

  @Input("uiIcon")
  public icon?: string;

  @Input("uiTheme")
  public theme?: UiThemeType;

  @Input("uiBgColor")
  public bgColor?: string;

  @Input("uiPattern")
  public pattern?: string;

  @Input("uiDisabled")
  @InputBoolean()
  public disabled?: boolean;

  @Input("uiText")
  public text?: string;

  @Input("uiTextColor")
  public textColor?: string;

  @Input("uiIconFull")
  @InputBoolean()
  public iconFull?: boolean;

  /**
   * @ignore
   */
  @Output()
  public readonly uiError = new EventEmitter<Event>();

  constructor(private uiConfigurationService: UiConfigurationService) {}

  /**
   * @ignore
   */
  public ngOnChanges(): void {
    const cdnUrl = this.uiConfigurationService.cdnUrl || "";
    this.patternSrc = this.pattern ? `${cdnUrl}/dist/img/patterns/${this.pattern}.png` : undefined;
    this.realAlt = this.alt == null ? "" : this.alt;
  }
}
